import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Moment from "react-moment";
import PrintPurchaseOrder from "../components/PrintPurchaseOrder";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePurchaseOrder,
  getPODeleteStatus,
  getPOSendStatus,
  getPoEsignPrepToken,
  getPoEsignUploadStatus,
  getPoUploadedDocUrl,
  getPurchaseOrderDoc,
  getPurchaseOrders,
  loadPurchaseOrders,
  loadUploadedPurchaseOrderDoc,
  purchaseOrderDocReceived,
  purchaseOrderEsignUploadReset,
  purchaseOrderUploadedDocReceived,
  sendPurchaseOrder,
  uploadPurchaseOrderToEsign,
  loadPoEsignLinks,
  getPoEsignLinks,
} from "../app/store/purchaseOrder";
import { getAlert, getConfirmAlert } from "../services/appAlerts";
import {
  getCurrentUser,
} from "../app/store/auth";
import config from "../config.json";

const PurchaseOrderPopup = (props) => {
  const {
    candidateObj,
    handlePurchaseShowHide,
    purchaseShowHide,
    workflowTask,
    poOrderType,
    item,
    handleSignUnsignPo,
    handleRefreshPoOrders,
    renderType,
    onBack,
    hideHeader
  } = props;

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [allPoTypes, setAllPoTypes] = useState([]);
  const [genShowHide, setGenShowHide] = useState(false);
  const [poAction, setPoAction] = useState("");
  const [poEsignLoading, setPoEsignLoading] = useState(false);
  const [loadingPoId, setLoadingPoId] = useState(null);
  const [sendLoadingPoId, setSendLoadingPoId] = useState(null);
  const [isLoadingPO, setIsLoadingPO] = useState(false);
  const [lastLoadedPerformerId, setLastLoadedPerformerId] = useState(null);

  const purchaseOrdersSelector = useSelector(getPurchaseOrders);
  const purchaseOrderDocSelector = useSelector(getPurchaseOrderDoc);
  const poSendStatusSelector = useSelector(getPOSendStatus);
  const pODeleteStatusSelector = useSelector(getPODeleteStatus);
  const poUploadedDocUrlSelector = useSelector(getPoUploadedDocUrl);
  const poEsignPrepTokenSelector = useSelector(getPoEsignPrepToken);
  const poEsignUploadStatusSelector = useSelector(getPoEsignUploadStatus);
  const poEsignLinks = useSelector(getPoEsignLinks);

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  const getEsignBaseUrl = () =>
    window.location.hostname === "localhost"
      ? "http://localhost:5173"
      : (process?.env?.REACT_APP_ESIGN_BASEURL || "http://localhost:5173");

  const getEsignApiBaseUrl = () =>
    process?.env?.REACT_APP_ESIGN_API_BASEURL ||
    process?.env?.REACT_APP_ESIGN_API_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:5005" : getEsignBaseUrl());

  const getWorkbenchApiBaseUrl = () => (process?.env?.REACT_APP_API_BASEURL || "").replace(/\/$/, "");

  const getPoEsignValue = (poOrder, key) =>
    poOrder?.[key] ?? poOrder?.[`e${key.substring(1)}`] ?? poOrder?.[`esign${key.replace(/^ESign/, "")}`];

  const getPoEsignDocumentId = (poOrder) =>
    getPoEsignValue(poOrder, "ESignDocumentId") || poOrder?.eSignDocumentId || poOrder?.esignDocumentId;

  const getPoEsignPreparationToken = (poOrder) =>
    getPoEsignValue(poOrder, "ESignPreparationToken") || poOrder?.eSignPreparationToken || poOrder?.esignPreparationToken;

  const getPoEsignStatus = (poOrder) =>
    getPoEsignValue(poOrder, "ESignStatus") || poOrder?.eSignStatus || poOrder?.esignStatus;

  const isPoEsignSigned = (poOrder) => {
    const status = (getPoEsignStatus(poOrder) || "").replace(/\s+/g, "").toLowerCase();
    return status === "signed" || status === "completed" || !!getPoEsignValue(poOrder, "ESignCompletedDateTime");
  };

  const getPoEsignBadgeDetails = (status, documentId) => {
    if (!status && !documentId) return null;
    const normalized = (status || "Draft").replace(/\s+/g, "").toLowerCase();

    switch (normalized) {
      case "signed":
      case "completed":
        return { text: "Signed", icon: "fa-check-circle", className: "done" };
      case "partiallysigned":
        return { text: "Partially Signed", icon: "fa-adjust", className: "pending" };
      case "sent":
        return { text: "Sent", icon: "fa-paper-plane-o", className: "pending" };
      case "rejected":
      case "cancelled":
      case "canceled":
      case "expired":
        return { text: status, icon: "fa-exclamation-circle", className: "danger" };
      default:
        return { text: "Draft", icon: "fa-pencil-square-o", className: "draft" };
    }
  };

  useEffect(() => {
    if (candidateObj?.performerid > 0 && lastLoadedPerformerId !== candidateObj.performerid && !isLoadingPO) {
      setIsLoadingPO(true);
      setLastLoadedPerformerId(candidateObj.performerid);
      dispatch(loadPurchaseOrders(candidateObj.performerid));
    }

    const poTypes = [];
    if (candidateObj.subcontractor?.fuid > 0 && (item?.partnerType === "Sub-Contractors" || item?.partnerType === "Subcontractor")) {
      poTypes.push({
        id: candidateObj?.subcontractor.fuid,
        name: "Subcontractor",
        value: "subcontractor",
      });
    } else if (candidateObj.recruiter?.fuid > 0 && item?.partnerType === "Recruiter") {
      poTypes.push({
        id: candidateObj.recruiter.fuid,
        name: "Recruiter",
        value: "recruiter",
      });
    }
    setAllPoTypes(poTypes);
  }, [candidateObj?.performerid, candidateObj?.subcontractor, candidateObj?.recruiter, item?.partnerType, dispatch]);

  useEffect(() => {
    if (purchaseOrdersSelector && purchaseOrdersSelector.length > 0) {
      setIsLoadingPO(false);
      let poType = "recruiter";
      if (item?.partnerType === "Sub-Contractors" || item?.partnerType === "Subcontractor") {
        poType = "subcontractor";
      }
      const filterPoOrder = purchaseOrdersSelector.filter(
        (x) => x.type === poType
      );
      setPurchaseOrders(filterPoOrder);
    } else if (purchaseOrdersSelector && purchaseOrdersSelector.length === 0) {
      setIsLoadingPO(false);
      setPurchaseOrders([]);
    }
  }, [purchaseOrdersSelector, item?.partnerType]);

  useEffect(() => {
    for (const order of purchaseOrders) {
      const esignToken = getPoEsignPreparationToken(order);
      if (esignToken && !poEsignLinks[order.id]) {
        dispatch(loadPoEsignLinks(order.id));
      }
    }
  }, [purchaseOrders, poEsignLinks, dispatch]);

  const handleGenShowHide = (action) => {
    if (action === "save") {
      setGenShowHide(false);
      setPoAction("");
      getAlert("", "Added Successfully", "success");
      if (candidateObj?.performerid > 0) {
        dispatch(loadPurchaseOrders(candidateObj.performerid));
        handleRefreshPoOrders();
      }
    } else {
      setGenShowHide(!genShowHide);
      setPoAction(action);
    }
  };

  const handleDownloadFile = async (item, type) => {
    dispatch(loadUploadedPurchaseOrderDoc(item.id, type));
  };

  const handleViewPoFile = (poOrder, type = "") => {
    const apiBaseUrl = getWorkbenchApiBaseUrl();
    const viewUrl = `${apiBaseUrl}/api/${config.GetPoOrderDocument}${poOrder.id}&type=${encodeURIComponent(type)}&inline=true`;
    window.open(viewUrl, "_blank");
  };

  useEffect(() => {
    if (purchaseOrderDocSelector?.size === 0) {
      getAlert("", "Document Not Found", "error");
      dispatch(purchaseOrderDocReceived(null));
    } else if (purchaseOrderDocSelector) {
      const url = window.URL.createObjectURL(purchaseOrderDocSelector);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      dispatch(purchaseOrderDocReceived(null));
    }
  }, [purchaseOrderDocSelector]);

  useEffect(() => {
    console.log("poUploadedDocUrlSelector", poUploadedDocUrlSelector);
    if (poUploadedDocUrlSelector && poUploadedDocUrlSelector?.uploadUrl) {
      window.open(poUploadedDocUrlSelector.uploadUrl, "_blank");
      dispatch(purchaseOrderUploadedDocReceived(null));
    }
  }, [poUploadedDocUrlSelector]);

  useEffect(() => {
    if (poEsignUploadStatusSelector === "success" && poEsignPrepTokenSelector && !isLoadingPO) {
      setPoEsignLoading(false);
      setLoadingPoId(null);
      window.open(`${getEsignBaseUrl()}/prepare/${poEsignPrepTokenSelector}`, "_blank");
      dispatch(purchaseOrderEsignUploadReset());
      if (candidateObj?.performerid > 0) {
        setIsLoadingPO(true);
        dispatch(loadPurchaseOrders(candidateObj.performerid));
        if (handleRefreshPoOrders) handleRefreshPoOrders();
      }
    } else if (poEsignUploadStatusSelector === "failed") {
      setPoEsignLoading(false);
      setLoadingPoId(null);
      getAlert("", "Failed to upload purchase order to eSign API.", "error");
      dispatch(purchaseOrderEsignUploadReset());
    }
  }, [poEsignUploadStatusSelector, poEsignPrepTokenSelector, candidateObj?.performerid, dispatch, handleRefreshPoOrders]);

  const handleSendPoThroughEsign = async (poOrder) => {
    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to send this Purchase Order through eSign?",
      "warning"
    );

    if (result.isConfirmed) {
      setPoEsignLoading(true);
      setLoadingPoId(poOrder.id);
      const frm = new FormData();
      frm.append("poId", poOrder.id);
      frm.append("ciid", poOrder.ciid || workflowTask?.ciid);
      frm.append("userId", currentUser?.id);
      dispatch(uploadPurchaseOrderToEsign(frm));
    }
  };

  const handleSendPoWithoutEsign = async (poOrder) => {
    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to send this Purchase Order without eSign?",
      "warning"
    );

    if (result.isConfirmed) {
      setSendLoadingPoId(poOrder.id);
      dispatch(sendPurchaseOrder(poOrder.id));
    }
  };

  const handleDeletePoOrder = async (id) => {
    const result = await getConfirmAlert(
      "Warning",
      "Are you sure you want to Delete this Purchase Order?",
      "warning"
    );
    if (result.isConfirmed) {
      dispatch(deletePurchaseOrder(id));
    }
  };

  useEffect(() => {
    if (poSendStatusSelector && !isLoadingPO) {
      setSendLoadingPoId(null);
      getAlert("", "Purchase Order Sent Successfully", "success");
      if (candidateObj?.performerid > 0) {
        setIsLoadingPO(true);
        dispatch(loadPurchaseOrders(candidateObj.performerid));
        if (handleRefreshPoOrders) handleRefreshPoOrders();
      }
    }
  }, [poSendStatusSelector, candidateObj?.performerid, dispatch, handleRefreshPoOrders]);

  useEffect(() => {
    if (pODeleteStatusSelector && !isLoadingPO) {
      getAlert("", "Deleted Successfully", "success");
      if (candidateObj?.performerid > 0) {
        setIsLoadingPO(true);
        dispatch(loadPurchaseOrders(candidateObj.performerid));
      }
    }
  }, [pODeleteStatusSelector, candidateObj?.performerid, dispatch]);

  const handlePoRefresh = (type) => {
    if (candidateObj?.performerid > 0) {
      dispatch(loadPurchaseOrders(candidateObj.performerid));
      //setUpdateCompStatus(true);
    }
  };

  const poBody = () => {

    return <div className="row">
      {poOrderType === "add" && (
        <div className="col-12 d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <button
              className="pop-action-btn generate"
              onClick={() => handleGenShowHide("generate")}
            >
              <i className="fa fa-plus-circle" /> Generate Purchase Order
            </button>
            <button
              className="pop-action-btn upload"
              onClick={() => handleGenShowHide("upload")}
            >
              <i className="fa fa-cloud-upload" /> Upload PO
            </button>
          </div>
        </div>
      )}

      <div className="col-md-12">
        {purchaseOrders.length === 0 ? (
          <div className="pop-empty">
            <div className="pop-empty-icon"><i className="fa fa-inbox" /></div>
            <h5>No purchase orders</h5>
            <p>Generate or upload a purchase order to send it for eSign.</p>
          </div>
        ) : (
          <>
            <div className="pop-section-label"><i className="fa fa-paper-plane" /> Purchase Orders</div>
            <div className="pop-doc-list">
              {purchaseOrders.map((item) => {
                const esignDocumentId = getPoEsignDocumentId(item);
                const esignPreparationToken = getPoEsignPreparationToken(item);
                const esignStatus = getPoEsignStatus(item);
                const esignBadge = getPoEsignBadgeDetails(esignStatus, esignDocumentId || esignPreparationToken);
                const esignBaseUrl = getEsignBaseUrl();
                const esignApiBaseUrl = getEsignApiBaseUrl();
                const esignToken = esignPreparationToken;
                const esignSigned = isPoEsignSigned(item);
                const badge = esignBadge || (item.sendStatus
                  ? { text: "Sent", icon: "fa-paper-plane-o", className: "pending" }
                  : { text: "Not Sent", icon: "fa-pencil-square-o", className: "draft" });

                const links = poEsignLinks[item.id] || {};
                const prepareUrl = links.prepare || `${esignBaseUrl}/prepare/${esignToken}`;
                const detailsUrl = links.details || `${esignBaseUrl}/documents/${esignToken}/details`;
                const logsUrl = links.logs || `${esignBaseUrl}/documents/${esignToken}/logs`;
                const visibleSignedUrl = links.visibleSigned || `${esignApiBaseUrl}/api/public/documents/${esignToken}/download/visible-signed?inline=true`;
                const finalSignedUrl = links.finalSigned || `${esignApiBaseUrl}/api/public/documents/${esignToken}/download/final-signed?inline=true`;

                return (
                  <div key={`po-${item.id}`} className="pop-doc-row">
                    <div className="pop-doc-icon">
                      <i className="fa fa-file-pdf-o" />
                    </div>

                    <div className="pop-doc-file">
                      <div className="pop-doc-title-line">
                        <span className="pop-doc-name">Purchase Order #{item.id}</span>
                        <button className="pop-btn pop-btn-sm pop-doc-left-view" onClick={() => handleViewPoFile(item, "")}>
                          <i className="fa fa-download" /> View
                        </button>
                      </div>
                      <div className="pop-doc-meta">
                        <span><i className="fa fa-calendar" /> <Moment format="MMM Do YYYY">{item.addedDateTime}</Moment></span>
                        <span><i className="fa fa-clock-o" /> <Moment format="h:mm a">{item.addedDateTime}</Moment></span>
                        {item.ipcsPoName && <span><i className="fa fa-file-o" /> {item.ipcsPoName}</span>}
                        {(esignToken || esignDocumentId) && (
                          <span className="pop-doc-esign-tag">
                            <i className="fa fa-pencil-square-o" /> eSign
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="pop-doc-actions-panel">
                      <span className="pop-action-slot pop-doc-status-stack">
                        <span className={`pop-status ${badge.className}`}>
                          <i className={`fa ${badge.icon}`} /> {badge.text}
                        </span>
                        {item.sentDateTime && (
                          <span className="pop-doc-status-date">
                            <Moment format="MMM Do YYYY">{item.sentDateTime}</Moment> · <Moment format="h:mm a">{item.sentDateTime}</Moment>
                          </span>
                        )}
                      </span>

                      <span className="pop-doc-actions-divider" />

                      {!esignToken ? (
                        !item.sendStatus && (
                          <span className="pop-action-slot dropdown">
                            <button
                              className="pop-btn pop-btn-sm dropdown-toggle"
                              type="button"
                              id={`poSendDropdown-${item.id}`}
                              data-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                              title="Send Options"
                              disabled={poEsignLoading || sendLoadingPoId === item.id}
                            >
                              {loadingPoId === item.id || sendLoadingPoId === item.id ? (
                                <><i className="fa fa-spinner fa-spin" /> Sending...</>
                              ) : (
                                <><i className="fa fa-paper-plane-o" /> Send</>
                              )}
                            </button>
                            <div className="dropdown-menu dropdown-menu-right" aria-labelledby={`poSendDropdown-${item.id}`}>
                              <button
                                className="dropdown-item text-left"
                                type="button"
                                onClick={() => handleSendPoWithoutEsign(item)}
                              >
                                <i className="fa fa-paper-plane-o mr-2" /> Send
                              </button>
                              <button
                                className="dropdown-item text-left"
                                type="button"
                                onClick={() => handleSendPoThroughEsign(item)}
                                disabled={poEsignLoading}
                              >
                                <i className="fa fa-pencil-square-o mr-2" /> Send to eSign
                              </button>
                            </div>
                          </span>
                        )
                      ) : (
                        <span className="pop-action-slot dropdown">
                          <button
                            className="pop-btn pop-btn-sm dropdown-toggle"
                            type="button"
                            id={`poEsignDropdown-${item.id}`}
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            title="eSign Options"
                          >
                            <i className="fa fa-pencil-square-o" /> eSign
                          </button>
                          <div className="dropdown-menu dropdown-menu-right" aria-labelledby={`poEsignDropdown-${item.id}`}>
                            <a className="dropdown-item" href={prepareUrl} target="_blank" rel="noopener noreferrer">
                              <i className="fa fa-pencil-square-o mr-2" /> Prepare
                            </a>
                            <a className="dropdown-item" href={detailsUrl} target="_blank" rel="noopener noreferrer">
                              <i className="fa fa-info-circle mr-2" /> Details
                            </a>
                            <a className="dropdown-item" href={logsUrl} target="_blank" rel="noopener noreferrer">
                              <i className="fa fa-history mr-2" /> Log
                            </a>
                            {esignSigned && (
                              <>
                                <div className="dropdown-divider" />
                                <a
                                  className="dropdown-item"
                                  href={visibleSignedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fa fa-file-pdf-o mr-2" /> Visible Signed
                                </a>
                                <a
                                  className="dropdown-item"
                                  href={finalSignedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fa fa-certificate mr-2" /> Digital Signed
                                </a>
                              </>
                            )}
                          </div>
                        </span>
                      )}

                      <span className="pop-action-slot">
                        <button
                          className="pop-btn pop-btn-sm pop-btn-ghost-danger"
                          onClick={() => handleDeletePoOrder(item.id)}
                          title="Delete PO"
                        >
                          <i className="fa fa-trash-o" />
                        </button>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  }

  const poHeader = () => (
    <div className="pop-header">
      <div className="pop-header-left">
        <div className="pop-icon-box">
          <i className="fa fa-file-text-o" />
        </div>
        <div className="pop-title-block">
          <h2 className="pop-title">Purchase Orders</h2>
          <p className="pop-subtitle">
            Generate, upload, and track purchase orders for {candidateObj?.performername} {candidateObj?.lastName}.
          </p>

          <div className="pop-header-meta">
            <span className="pop-meta-pill meta-id">
              <i className="fa fa-id-badge" /> ID: {candidateObj?.performerid}
            </span>
            <span className="pop-meta-pill meta-person">
              <i className="fa fa-user" /> {candidateObj?.performername} {candidateObj?.lastName}
            </span>
            {candidateObj?.performeremail && (
              <span className="pop-meta-pill meta-email">
                <i className="fa fa-envelope-o" /> {candidateObj.performeremail}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="pop-header-right">
        <button
          type="button"
          className="pop-action-btn back"
          onClick={() => onBack ? onBack() : handlePurchaseShowHide("")}
        >
          <i className="fa fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="pop-action-btn refresh mr-2"
          onClick={() => handlePoRefresh("")}
          title="Refresh List"
        >
          <i className="fa fa-refresh" /> Refresh
        </button>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <style>{`
        /* ── Scoped Style for PurchaseOrderPopup ── */
        @keyframes popFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Header Section (Document Style) ── */
        .pop-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          flex-wrap: wrap; gap: 18px; padding: 22px 24px 18px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border-bottom: 1px solid #f1f5f9; margin-bottom: 20px;
          border-radius: 12px 12px 0 0;
        }
        .pop-header-left { display: flex; align-items: flex-start; gap: 14px; flex: 1; }
        .pop-icon-box {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #0f766e 0%, #2563eb 100%);
          display: flex; align-items: center; justify-content: center;
          color: #ffffff; font-size: 18px; flex-shrink: 0;
          box-shadow: 0 8px 16px rgba(37, 99, 235, 0.2);
        }
        .pop-title-block { min-width: 0; }
        .pop-kicker {
          display: inline-flex; align-items: center; gap: 6px;
          margin: 0 0 5px; color: #2563eb; font-size: 11px;
          font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
        }
        .pop-title { font-size: 18px; font-weight: 800; color: #0f172a; margin: 0; line-height: 1.2; }
        .pop-subtitle { font-size: 13px; color: #64748b; margin: 6px 0 0; }
        
        .pop-header-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
        .pop-header-right { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
        .pop-meta-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 99px; background: #ffffff;
          border: 1px solid #e2e8f0; color: #475569; font-size: 12px; font-weight: 700;
        }
        .pop-meta-pill i { font-size: 11px; }
        .pop-meta-pill.meta-id i { color: #1d4ed8; }
        .pop-meta-pill.meta-person i { color: #047857; }
        .pop-meta-pill.meta-email i { color: #0369a1; }

        .pop-container {
          animation: popFadeIn 0.3s ease both;
          width: 100%;
          background: #ffffff;
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
          margin-bottom: 10px;
        }
        .pop-content-body { padding: 24px 24px 24px; }

        /* ── Action Buttons ── */
        .pop-action-btn {
          min-height: 40px; display: inline-flex; align-items: center; justify-content: center;
          gap: 8px; font-size: 12.5px; font-weight: 600; border-radius: 8px;
          cursor: pointer; transition: all 0.2s ease;
          padding: 8px 16px; border: 1.5px solid transparent; outline: none;
        }
        .pop-action-btn.generate {
          background: #2563eb; color: white;
        }
        .pop-action-btn.generate:hover {
          background: #1d4ed8; transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        }
        .pop-action-btn.upload {
          background: #ffffff; color: #475569; border-color: #e2e8f0;
        }
        .pop-action-btn.upload:hover {
          background: #f8fafc; border-color: #cbd5e1; color: #1e293b;
          transform: translateY(-1px);
        }
        .pop-action-btn.refresh {
          background: #ffffff; color: #475569; border-color: #cbd5e1;
        }
        .pop-action-btn.refresh:hover {
          background: #f8fafc; border-color: #94a3b8; color: #0f172a;
          transform: translateY(-1px);
        }
        .pop-action-btn.back {
          background: #ffffff; color: #475569; border-color: #cbd5e1;
        }
        .pop-action-btn.back:hover {
          background: #f8fafc; border-color: #94a3b8; color: #1e293b;
        }

        /* Document-style PO rows */
        .pop-section-label {
          display: inline-flex; align-items: center; gap: 8px;
          margin: 0 0 12px; color: #334155; font-size: 12px;
          font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em;
        }
        .pop-doc-list {
          display: flex; flex-direction: column; gap: 8px;
        }
        .pop-doc-row {
          display: grid; grid-template-columns: 34px minmax(0, 1fr) auto;
          align-items: center; gap: 12px; padding: 16px;
          background: #ffffff; border: 1px solid #dbe3ef; border-radius: 10px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
          position: relative;
        }
        .pop-doc-row:hover {
          border-color: #bfdbfe;
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.08);
          transform: translateY(-1px);
          z-index: 2;
        }
        .pop-doc-row:focus-within,
        .pop-doc-row:has(.show) {
          z-index: 10;
        }
        .pop-doc-icon {
          width: 34px; height: 34px; border-radius: 8px;
          display: inline-flex; align-items: center; justify-content: center;
          background: #eff6ff; color: #2563eb; border: 1px solid #dbeafe;
          font-size: 15px;
        }
        .pop-doc-file { min-width: 0; }
        .pop-doc-title-line {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .pop-doc-name {
          padding: 0; margin: 0;
          color: #0f172a; font-weight: 800; font-size: 14px;
          display: inline-flex; align-items: center; gap: 6px;
          text-align: left; line-height: 1.25;
        }
        .pop-doc-meta {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
          margin-top: 5px; color: #64748b; font-size: 12px; font-weight: 600;
        }
        .pop-doc-meta span {
          display: inline-flex; align-items: center; gap: 5px;
          min-width: 0; max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .pop-doc-esign-tag {
          width: fit-content; padding: 2px 8px; border-radius: 99px;
          background: rgba(37, 99, 235, 0.08); border: 1px solid rgba(37, 99, 235, 0.15);
          color: #1d4ed8; font-size: 11px; font-weight: 700;
        }
        .pop-doc-actions-panel {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 10px; align-self: center; min-width: 620px;
        }
        .pop-action-slot {
          display: inline-flex; align-items: center; justify-content: flex-end; min-width: 0;
        }
        .pop-doc-status-stack {
          flex-direction: column; align-items: center; justify-content: center; gap: 4px;
        }
        .pop-doc-status-date {
          color: #94a3b8; font-size: 11px; font-weight: 600;
          line-height: 1.1; text-align: center; white-space: nowrap;
        }
        .pop-doc-actions-divider {
          width: 1px; height: 24px; background: #e2e8f0; margin: 0 4px;
          align-self: center;
        }
        .pop-btn {
          min-height: 38px; display: inline-flex; align-items: center; justify-content: center;
          gap: 7px; font-size: 12px; font-weight: 700; border-radius: 8px;
          cursor: pointer; transition: all 0.15s ease; padding: 8px 13px;
          border: 1px solid #dbe3ef; background: #ffffff; color: #334155;
          text-decoration: none !important; white-space: nowrap;
        }
        .pop-btn:hover {
          background: #f8fafc; border-color: #bfdbfe; color: #1d4ed8;
          transform: translateY(-1px);
        }
        .pop-btn-sm { min-height: 38px; padding: 8px 13px; font-size: 12px; }
        .pop-btn.pop-btn-sm.pop-doc-left-view {
          min-height: 26px; height: 26px; padding: 3px 9px; font-size: 11.5px;
          border-radius: 7px; line-height: 1.1; gap: 5px;
        }
        .pop-btn-primary {
          background: #2563eb; border-color: #2563eb; color: #ffffff;
        }
        .pop-btn-primary:hover {
          background: #1d4ed8; border-color: #1d4ed8; color: #ffffff;
        }
        .pop-btn-ghost-danger {
          width: 38px; padding: 0; color: #dc2626; border-color: #fee2e2; background: #fff;
        }
        .pop-btn-ghost-danger:hover {
          color: #b91c1c; background: #fef2f2; border-color: #fecaca;
        }
        .pop-btn:disabled {
          opacity: 0.6; cursor: not-allowed; transform: none;
        }
        .pop-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          min-height: 180px; border: 1px dashed #cbd5e1; border-radius: 12px;
          background: #f8fafc; color: #64748b; text-align: center; padding: 28px;
        }
        .pop-empty-icon {
          width: 42px; height: 42px; border-radius: 12px; display: inline-flex;
          align-items: center; justify-content: center; background: #eff6ff;
          color: #2563eb; margin-bottom: 10px; font-size: 18px;
        }
        .pop-empty h5 { margin: 0 0 4px; color: #0f172a; font-weight: 800; }
        .pop-empty p { margin: 0; font-size: 13px; }

        /* ── Table Styling ── */
        .pop-table {
          width: 100% !important; border-collapse: separate !important; border-spacing: 0 2px !important;
        }
        .pop-thead th {
          background: #f8fafc !important; color: #475569 !important; font-size: 10.5px !important;
          font-weight: 700 !important; text-transform: uppercase !important; letter-spacing: 0.8px !important;
          padding: 12px 16px !important; border: none !important; border-bottom: 2px solid #e2e8f0 !important;
        }
        .pop-table-row {
          background: #ffffff !important; transition: all 0.2s ease !important;
        }
        .pop-table-row:hover {
          background: #f8fafc !important;
        }
        .pop-table-row td {
          padding: 12px 16px !important; font-size: 13px !important; color: #334155 !important;
          border: none !important; border-bottom: 1px solid #f1f5f9 !important; vertical-align: middle !important;
        }

        /* Status Badges */
        .pop-status {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 99px; font-size: 11px; font-weight: 700;
        }
        .pop-status.pending { background: #fff7ed; color: #ea580c; border: 1px solid #ffedd5; }
        .pop-status.done { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }
        .pop-status.draft { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
        .pop-status.danger { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        
        .pop-link-btn {
          color: #2563eb; font-weight: 600; text-decoration: none !important;
          display: inline-flex; align-items: center; gap: 4px;
          transition: color 0.2s ease; cursor: pointer; border: none; background: none; padding: 0;
        }
        .pop-link-btn:disabled { color: #94a3b8; cursor: not-allowed; }
        .pop-link-btn:hover { color: #1d4ed8; }
        .pop-link-btn.delete { color: #dc2626; }
        .pop-link-btn.delete:hover { color: #b91c1c; }

        @media (max-width: 1200px) {
          .pop-modal-xl { max-width: 95% !important; }
        }
        @media (max-width: 900px) {
          .pop-doc-row {
            grid-template-columns: 34px minmax(0, 1fr);
          }
          .pop-doc-actions-panel {
            grid-column: 1 / -1;
            min-width: 0;
            justify-content: flex-start;
            flex-wrap: wrap;
            padding-left: 46px;
          }
          .pop-doc-status-stack {
            align-items: flex-start;
          }
          .pop-doc-actions-divider {
            display: none;
          }
        }
        @media (max-width: 560px) {
          .pop-doc-row {
            grid-template-columns: 1fr;
          }
          .pop-doc-icon {
            display: none;
          }
          .pop-doc-actions-panel {
            padding-left: 0;
          }
        }
      `}</style>

      {renderType === "generate" && (
        <div className="pop-container">
          {hideHeader !== true && poHeader()}
          <div className="pop-content-body">
            {poBody()}
          </div>
        </div>
      )}

      {renderType !== "generate" && (
        <Modal
          show={purchaseShowHide}
          size="xl"
          centered
          onHide={() => handlePurchaseShowHide("")}
          dialogClassName="pop-modal-xl"
        >
          <Modal.Body className="p-0">
            <div className="pop-container m-0" style={{ border: "none", borderRadius: "12px" }}>
              {hideHeader !== true && poHeader()}
              <div className="pop-content-body">
                {poBody()}
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <PrintPurchaseOrder
        genShowHide={genShowHide}
        handleGenShowHide={handleGenShowHide}
        candidateObj={candidateObj}
        workflowTask={workflowTask}
        poAction={poAction}
        allPoTypes={allPoTypes}
      ></PrintPurchaseOrder>
    </React.Fragment>
  );
};

export default PurchaseOrderPopup;
